import { useQuery } from "@apollo/client";
import { Combobox } from "@/components/Combobox";
import { type ComboboxOption } from "@/components/Combobox/types";
import { graphql, type ResultOf } from "@/graphql";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { removeNothings } from "@/lib/utils/arrays";
import { sortOptionsByLabel } from "@/lib/utils/sorting";

type Props = {
  id?: string;
  selected: Array<string>;
  onSelect: (values: Array<string>) => void;
  className?: string;
  placeholder: string;
  inputRef?: React.Ref<HTMLButtonElement>;
  isModal?: boolean;
};

export type AccreditationsData = ResultOf<typeof accreditationsQuery>;

export function AccreditationsPicker({ id, selected, onSelect, className, placeholder, inputRef, isModal }: Props) {
  const { activeBusinessUnitId } = useActiveBusinessUnit();
  const { data, loading, error } = useQuery(accreditationsQuery, {
    variables: { businessUnitId: activeBusinessUnitId || "" },
  });

  const options: Array<ComboboxOption> = getAccreditationOptions(data);

  return (
    <Combobox
      id={id}
      name="accreditations"
      options={options}
      selected={selected}
      onChange={onSelect}
      loading={loading}
      error={error}
      type="multi"
      className={className}
      placeholder={placeholder}
      inputRef={inputRef}
      isModal={isModal}
    />
  );
}

export const getAccreditationOptions = (data: AccreditationsData | undefined) => {
  const options = sortOptionsByLabel(
    removeNothings(data?.supplyChainBookingAccreditations || []).map((accreditation) => {
      return {
        label: accreditation.label,
        value: accreditation.id,
      };
    }),
  );

  return options;
};

export const accreditationsQuery = graphql(`
  query SupplyChainBookingAccreditations($businessUnitId: ID!) {
    supplyChainBookingAccreditations(businessUnit: $businessUnitId) {
      status
      label
      id
    }
  }
`);
